import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import MenuUploadForm from './MenuUploadForm';
import MenuUrlExtractor from './MenuUrlExtractor';
import MenuImageImport from './MenuImageImport';
import ConfirmModal from './ConfirmModal';
import { toast } from 'react-hot-toast';
import './AffiliateDashboard.css';
import { useDebounce } from 'use-debounce';
import ImpactService from '../services/impactService';
import { format, parseISO } from 'date-fns';
import {
  getCoachExercises, createCoachExercise, updateCoachExercise, deleteCoachExercise, publishCoachExercise,
  getCoachWorkouts, createCoachWorkout, updateCoachWorkout, deleteCoachWorkout, publishCoachWorkout,
  getCoachPrograms, createCoachProgram, updateCoachProgram, deleteCoachProgram, publishCoachProgram,
  getExerciseLibrary, getCoachFitnessStats, assignToClient, updateFitnessPageSettings
} from '../services/coachFitnessService';

const AffiliateDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showUrlExtractor, setShowUrlExtractor] = useState(false);
  const [showImageImport, setShowImageImport] = useState(false);
  const [showImportExplainer, setShowImportExplainer] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [menus, setMenus] = useState([]);
  const [importProgress, setImportProgress] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [payments, setPayments] = useState({});
  const [usageStats, setUsageStats] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    businessName: '',
    businessType: '',
    website: '',
    socialMedia: { instagram: '', facebook: '', twitter: '', tiktok: '' },
    paymentInfo: { email: '', type: 'PAYPAL' }
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: format(new Date(new Date().setMonth(new Date().getMonth() - 3)), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const user = JSON.parse(localStorage.getItem('user'));
  const navigate = useNavigate();
  const location = useLocation();
  const fitnessSectionRef = useRef(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  const formSectionRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, menuId: null, title: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const [importConfirm, setImportConfirm] = useState({ isOpen: false, callback: null });
  const [unmigratedMenusCount, setUnmigratedMenusCount] = useState(0);
  const [isMigratingMenus, setIsMigratingMenus] = useState(false);
  const [isPublishing, setIsPublishing] = useState(null);
  const [editConfirm, setEditConfirm] = useState({ isOpen: false, item: null });

  // Page customization state
  const [pageSettings, setPageSettings] = useState({
    slug: '',
    headline: '',
    bio: '',
    heroImage: '',
    logoImage: '',
    theme: 'classic'
  });
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [savingPageSettings, setSavingPageSettings] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Fitness Coach state
  const [fitnessStats, setFitnessStats] = useState(null);
  const [fitnessExercises, setFitnessExercises] = useState([]);
  const [fitnessWorkouts, setFitnessWorkouts] = useState([]);
  const [fitnessPrograms, setFitnessPrograms] = useState([]);
  const [fitnessTab, setFitnessTab] = useState('exercises');
  const [showExerciseForm, setShowExerciseForm] = useState(false);
  const [showWorkoutForm, setShowWorkoutForm] = useState(false);
  const [showProgramForm, setShowProgramForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [editingProgram, setEditingProgram] = useState(null);
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [fitnessLoading, setFitnessLoading] = useState(false);
  const [exerciseForm, setExerciseForm] = useState({ name: '', category: '', primaryMuscles: '', equipment: '', description: '', youtubeUrl: '' });
  const [workoutForm, setWorkoutForm] = useState({ name: '', difficulty: 'Intermediate', duration: 30, exercises: [] });
  const [programForm, setProgramForm] = useState({ name: '', description: '', difficulty: 'Intermediate', durationWeeks: 4, workoutsPerWeek: 3, workouts: [] });
  const [fitnessPageForm, setFitnessPageForm] = useState({ headline: '', bio: '' });
  const [savingFitnessPage, setSavingFitnessPage] = useState(false);
  const [fitnessDeleteConfirm, setFitnessDeleteConfirm] = useState({ isOpen: false, type: '', id: null, name: '' });
  const [isDeletingFitness, setIsDeletingFitness] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({ clientId: '', type: 'workout', itemId: '', scheduledDate: '' });
  const [stripeConnectStatus, setStripeConnectStatus] = useState(null);
  const [stripeConnectLoading, setStripeConnectLoading] = useState(false);

  // Scroll to form section with a small offset
  const scrollToForm = () => {
    setTimeout(() => {
      if (formSectionRef.current) {
        formSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  useEffect(() => {
    fetchData();
    fetchOrders();
    fetchPayments();
    fetchSubscriptionStatus();
    // Sync Impact stats on initial load (silently)
    ImpactService.syncStats().catch(() => {});
  }, [dateRange]);

  // Fetch fitness data when metrics load and specialization is fitness/both
  useEffect(() => {
    if (metrics && ['fitness', 'both'].includes(metrics.specialization)) {
      fetchFitnessData();
      fetchStripeConnectStatus();
    }
  }, [metrics?.specialization]);

  // Auto-scroll to fitness section for fitness coaches
  useEffect(() => {
    if (metrics && metrics.specialization === 'fitness' && fitnessSectionRef.current) {
      setTimeout(() => fitnessSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [metrics?.specialization]);
  
  const fetchFitnessData = async () => {
    setFitnessLoading(true);
    try {
      const [stats, exercises, workouts, programs, library] = await Promise.all([
        getCoachFitnessStats().catch(() => null),
        getCoachExercises().catch(() => []),
        getCoachWorkouts().catch(() => []),
        getCoachPrograms().catch(() => []),
        getExerciseLibrary({ limit: 500 }).catch(() => [])
      ]);
      setFitnessStats(stats);
      setFitnessExercises(Array.isArray(exercises) ? exercises : exercises?.exercises || []);
      setFitnessWorkouts(Array.isArray(workouts) ? workouts : workouts?.workouts || []);
      setFitnessPrograms(Array.isArray(programs) ? programs : programs?.programs || []);
      setExerciseLibrary(Array.isArray(library) ? library : library?.exercises || []);
      if (metrics?.fitnessPageSettings) {
        setFitnessPageForm({
          headline: metrics.fitnessPageSettings.headline || '',
          bio: metrics.fitnessPageSettings.bio || ''
        });
      }
    } catch (error) {
      console.error('Error fetching fitness data:', error);
    } finally {
      setFitnessLoading(false);
    }
  };

  const fetchStripeConnectStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe-connect/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStripeConnectStatus(await response.json());
      }
    } catch (error) {
      console.error('Error fetching Stripe Connect status:', error);
    }
  };

  const handleStripeOnboard = async () => {
    setStripeConnectLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe-connect/onboard`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url;
      } else {
        const err = await response.json();
        toast.error(err.error || 'Failed to start onboarding');
      }
    } catch (error) {
      toast.error('Failed to connect Stripe');
    } finally {
      setStripeConnectLoading(false);
    }
  };

  const handleStripeDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/stripe-connect/dashboard-link`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const { url } = await response.json();
        window.open(url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const handleExerciseSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...exerciseForm,
        primaryMuscles: exerciseForm.primaryMuscles.split(',').map(m => m.trim()).filter(Boolean),
        equipment: exerciseForm.equipment ? [exerciseForm.equipment] : []
      };
      if (editingExercise) {
        await updateCoachExercise(editingExercise._id, data);
        toast.success('Exercise updated');
      } else {
        await createCoachExercise(data);
        toast.success('Exercise created');
      }
      setExerciseForm({ name: '', category: '', primaryMuscles: '', equipment: '', description: '', youtubeUrl: '' });
      setEditingExercise(null);
      setShowExerciseForm(false);
      fetchFitnessData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleWorkoutSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingWorkout) {
        await updateCoachWorkout(editingWorkout._id, workoutForm);
        toast.success('Workout updated');
      } else {
        await createCoachWorkout(workoutForm);
        toast.success('Workout created');
      }
      setWorkoutForm({ name: '', difficulty: 'Intermediate', duration: 30, exercises: [] });
      setEditingWorkout(null);
      setShowWorkoutForm(false);
      fetchFitnessData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleProgramSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProgram) {
        await updateCoachProgram(editingProgram._id, programForm);
        toast.success('Program updated');
      } else {
        await createCoachProgram(programForm);
        toast.success('Program created');
      }
      setProgramForm({ name: '', description: '', difficulty: 'Intermediate', durationWeeks: 4, workoutsPerWeek: 3, workouts: [] });
      setEditingProgram(null);
      setShowProgramForm(false);
      fetchFitnessData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleFitnessDelete = async () => {
    setIsDeletingFitness(true);
    try {
      const { type, id } = fitnessDeleteConfirm;
      if (type === 'exercise') await deleteCoachExercise(id);
      else if (type === 'workout') await deleteCoachWorkout(id);
      else if (type === 'program') await deleteCoachProgram(id);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted`);
      fetchFitnessData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsDeletingFitness(false);
      setFitnessDeleteConfirm({ isOpen: false, type: '', id: null, name: '' });
    }
  };

  const handlePublish = async (type, id) => {
    try {
      if (type === 'exercise') await publishCoachExercise(id);
      else if (type === 'workout') await publishCoachWorkout(id);
      else if (type === 'program') await publishCoachProgram(id);
      toast.success('Submitted for review!');
      fetchFitnessData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSaveFitnessPage = async () => {
    setSavingFitnessPage(true);
    try {
      await updateFitnessPageSettings(fitnessPageForm);
      toast.success('Fitness page updated');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingFitnessPage(false);
    }
  };

  const handleAssignToClient = async (e) => {
    e.preventDefault();
    try {
      await assignToClient(assignForm.clientId, {
        type: assignForm.type,
        [`${assignForm.type}Id`]: assignForm.itemId,
        scheduledDate: assignForm.scheduledDate
      });
      toast.success('Assigned to client!');
      setShowAssignModal(false);
      setAssignForm({ clientId: '', type: 'workout', itemId: '', scheduledDate: '' });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const startEditExercise = (ex) => {
    setExerciseForm({
      name: ex.name || '',
      category: ex.category || '',
      primaryMuscles: (ex.primaryMuscles || []).join(', '),
      equipment: (ex.equipment || [])[0] || '',
      description: ex.description || '',
      youtubeUrl: ex.youtubeUrl || ''
    });
    setEditingExercise(ex);
    setShowExerciseForm(true);
  };

  const startEditWorkout = (w) => {
    setWorkoutForm({
      name: w.name || '',
      difficulty: w.difficulty || 'Intermediate',
      duration: w.duration || 30,
      exercises: w.exercises || []
    });
    setEditingWorkout(w);
    setShowWorkoutForm(true);
  };

  const startEditProgram = (p) => {
    setProgramForm({
      name: p.name || '',
      description: p.description || '',
      difficulty: p.difficulty || 'Intermediate',
      durationWeeks: p.durationWeeks || 4,
      workoutsPerWeek: p.workoutsPerWeek || 3,
      workouts: p.workouts || []
    });
    setEditingProgram(p);
    setShowProgramForm(true);
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionInfo(data);
      }
    } catch (err) {
      console.error('Error fetching subscription status:', err);
    }
  };

  const getAffiliateTierBadge = () => {
    const tier = subscriptionInfo?.affiliate?.tier || 'free';
    const tierLabels = {
      free: { label: 'Free Creator', className: 'tier-affiliate-free' },
      pro: { label: 'Pro Creator', className: 'tier-affiliate-pro' }
    };
    return tierLabels[tier] || tierLabels.free;
  };

  useEffect(() => {
    // Filter menu items based on search term
    const filtered = menuItems.filter(item => {
      const matchesSearch = !debouncedSearchTerm || 
        item.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (item.ingredients && item.ingredients.some(ingredient => 
          (typeof ingredient === 'object' && ingredient.name 
            ? ingredient.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            : String(ingredient).toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
        ));

      return matchesSearch;
    });
    
    setFilteredMenuItems(filtered);
  }, [debouncedSearchTerm, menuItems]);

  // Initialize profile form and page settings when metrics load
  useEffect(() => {
    if (metrics) {
      setProfileForm({
        businessName: metrics.businessName || '',
        businessType: metrics.businessType || '',
        website: metrics.website || '',
        socialMedia: {
          instagram: metrics.socialMedia?.instagram || '',
          facebook: metrics.socialMedia?.facebook || '',
          twitter: metrics.socialMedia?.twitter || '',
          tiktok: metrics.socialMedia?.tiktok || ''
        },
        paymentInfo: {
          email: metrics.paymentInfo?.email || '',
          type: metrics.paymentInfo?.type || 'PAYPAL'
        }
      });
      setPageSettings({
        slug: metrics.slug || '',
        headline: metrics.pageSettings?.headline || '',
        bio: metrics.pageSettings?.bio || '',
        heroImage: metrics.pageSettings?.heroImage || '',
        logoImage: metrics.pageSettings?.logoImage || '',
        theme: metrics.pageSettings?.theme || 'classic'
      });
    }
  }, [metrics]);

  // Add polling effect for import progress
  useEffect(() => {
    let pollInterval;

    if (isImporting) {
      pollInterval = setInterval(async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/import-progress`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!response.ok) {
            throw new Error('Failed to fetch import progress');
          }

          const data = await response.json();
          setImportProgress(data);

          // Stop polling when import is complete or has errored
          if (data.status === 'complete' || data.status === 'error') {
            setIsImporting(false);
            // Clear progress after 5 seconds
            setTimeout(() => setImportProgress(null), 5000);
            // Refresh the menu list
            fetchData();
          }
        } catch (error) {
          console.error('Error polling import progress:', error);
          setIsImporting(false);
          setImportProgress({
            status: 'error',
            message: 'Failed to fetch import progress',
            error: error.message,
            progress: 100
          });
        }
      }, 2000); // Poll every 2 seconds
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [isImporting]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Fetch metrics, menu items, and usage stats in parallel
      const [metricsResponse, menuItemsResponse, usageResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/affiliate/metrics`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/affiliate/menu-items`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`${process.env.REACT_APP_API_URL}/affiliate/usage-stats`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      ]);

      if (!metricsResponse.ok || !menuItemsResponse.ok) {
        throw new Error('Failed to fetch affiliate data');
      }

      const [metricsData, menuItemsData] = await Promise.all([
        metricsResponse.json(),
        menuItemsResponse.json()
      ]);

      // Usage stats is optional - don't fail if it doesn't work
      if (usageResponse.ok) {
        const usageData = await usageResponse.json();
        setUsageStats(usageData);
      }

      setMetrics(metricsData);
      setMenuItems(menuItemsData);
      setFilteredMenuItems(menuItemsData);
      setLoading(false);
      
      // Check for unmigrated personal menus
      checkUnmigratedMenus();
    } catch (err) {
      console.error('Error fetching affiliate data:', err);
      setError(err.message);
      setLoading(false);
    }
  };
  
  // Check if user has personal menus that can be migrated
  const checkUnmigratedMenus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/my-menus`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnmigratedMenusCount(data.unmigratedPersonalMenusCount || 0);
      }
    } catch (err) {
      console.error('Error checking unmigrated menus:', err);
    }
  };
  
  // Migrate personal menus to affiliate dashboard
  const handleMigratePersonalMenus = async () => {
    if (isMigratingMenus) return;
    
    setIsMigratingMenus(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/migrate-personal-menus`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setUnmigratedMenusCount(0);
        fetchData(); // Refresh menu list
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to migrate menus');
      }
    } catch (err) {
      console.error('Error migrating menus:', err);
      toast.error('Failed to migrate personal menus');
    } finally {
      setIsMigratingMenus(false);
    }
  };
  
  // Page Settings helpers
  const checkSlugAvailability = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }
    setCheckingSlug(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/check-slug/${slug}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSlugAvailable(data.available ? true : data.reason || false);
    } catch {
      setSlugAvailable(null);
    } finally {
      setCheckingSlug(false);
    }
  };

  const handleSavePageSettings = async () => {
    setSavingPageSettings(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/page-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pageSettings)
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save');
      }
      const data = await response.json();
      setPageSettings(prev => ({ ...prev, slug: data.slug, ...data.pageSettings }));
      toast.success('Page settings saved!');
    } catch (err) {
      toast.error(err.message || 'Failed to save page settings');
    } finally {
      setSavingPageSettings(false);
    }
  };

  const handleUploadPageImage = async (file, imageType) => {
    const setter = imageType === 'logo' ? setUploadingLogo : setUploadingHero;
    setter(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);
      formData.append('imageType', imageType);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/upload-page-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();
      setPageSettings(prev => ({
        ...prev,
        [imageType === 'logo' ? 'logoImage' : 'heroImage']: data.imageUrl
      }));
      toast.success(`${imageType === 'logo' ? 'Logo' : 'Cover image'} uploaded!`);
    } catch (err) {
      toast.error(err.message || 'Failed to upload image');
    } finally {
      setter(false);
    }
  };

  const STOCK_HERO_IMAGES = [
    'https://storage.googleapis.com/freshmenulab/stock_hero_images/cooking-kitchen-1.jpg',
    'https://storage.googleapis.com/freshmenulab/stock_hero_images/fresh-ingredients-2.jpg',
    'https://storage.googleapis.com/freshmenulab/stock_hero_images/colorful-dishes-3.jpg',
    'https://storage.googleapis.com/freshmenulab/stock_hero_images/rustic-table-4.jpg',
    'https://storage.googleapis.com/freshmenulab/stock_hero_images/modern-plating-5.jpg',
    'https://storage.googleapis.com/freshmenulab/stock_hero_images/herbs-spices-6.jpg',
    'https://storage.googleapis.com/freshmenulab/stock_hero_images/baking-bread-7.jpg',
    'https://storage.googleapis.com/freshmenulab/stock_hero_images/healthy-bowl-8.jpg',
  ];

  const THEME_OPTIONS = [
    { id: 'classic', label: 'Classic', desc: 'Clean white, green accent' },
    { id: 'modern', label: 'Modern', desc: 'Bold dark, teal accent' },
    { id: 'warm', label: 'Warm', desc: 'Cream, orange accent' },
    { id: 'fresh', label: 'Fresh', desc: 'Green gradients, light' },
    { id: 'dark', label: 'Dark', desc: 'Dark, purple accent' },
  ];

  // Publish a menu (submit for moderation)
  const handlePublishMenu = async (menuId) => {
    if (isPublishing) return;
    
    setIsPublishing(menuId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/menu-items/${menuId}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Menu submitted for review!');
        
        // Update the menu item in state
        setMenuItems(prev => prev.map(item => 
          item._id === menuId 
            ? { ...item, moderation_status: 'PENDING' }
            : item
        ));
        setFilteredMenuItems(prev => prev.map(item => 
          item._id === menuId 
            ? { ...item, moderation_status: 'PENDING' }
            : item
        ));
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to publish menu');
      }
    } catch (err) {
      console.error('Error publishing menu:', err);
      toast.error('Failed to publish menu');
    } finally {
      setIsPublishing(null);
    }
  };

  const syncImpactStats = async () => {
    try {
      await ImpactService.syncStats();
      // Refresh data to get updated stats
      fetchData();
      toast.success('Impact.com stats synced successfully');
    } catch (error) {
      console.error('Error syncing Impact stats:', error);
      toast.error('Failed to sync Impact.com stats');
    }
  };

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setProfileForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setProfileForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileForm)
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const data = await response.json();
      toast.success('Profile updated successfully!');
      setIsEditingProfile(false);
      
      // Refresh metrics to show updated data
      fetchData();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setOrdersLoading(true);
      const orders = await ImpactService.getAffiliateOrders();
      
      // Transform orders to include affiliate-specific payout calculations
      const processedOrders = orders.map(order => ({
        ...order,
        // Calculate affiliate's actual payout based on their commission rate
        affiliatePayout: parseFloat(order.sale_amount) * user.commissionRate
      }));
      
      setOrders(processedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/affiliate/payments?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch payments');
      }

      const data = await response.json();
      console.log('Fetched payments:', data);
      // Store both payments and summary separately
      setPayments({
        items: data.payments || [],
        summary: data.summary || { totalEarnings: 0, totalPaid: 0, unpaidBalance: 0 }
      });
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to fetch payment history');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const calculatePayout = (saleAmount) => {
    const commissionRate = metrics?.commissionRate || 0.02; // Default to 2% if not set
    return saleAmount * commissionRate;
  };

  const handleUploadSuccess = (newItem) => {
    // Clear the editing state and forms
    setEditingItem(null);
    setShowUploadForm(false);
    
    // Fetch fresh data to ensure we have the latest state
    fetchData();
    
    // Show success message
    toast.success('Menu item uploaded successfully!');
  };

  const handleExtractSuccess = (extractedData) => {
    // Normalize contentUrl and cuisine fields for the form
    const normalizedData = {
      ...extractedData,
      contentUrl: extractedData.contentUrl || extractedData.url || extractedData.sourceUrl || '',
      image: extractedData.image || extractedData.imageUrl || extractedData.mainImageUrl || '',
      cuisine: extractedData.cuisine || '',
    };
    setEditingItem(normalizedData);
    setShowUploadForm(true);
    setShowUrlExtractor(false);
    setShowImageImport(false);
    setShowImportExplainer(false);
    scrollToForm();
  };

  const handleImageImportSuccess = (extractedData) => {
    // Normalize the extracted data for the form
    const normalizedData = {
      ...extractedData,
      contentUrl: extractedData.contentUrl || '',
      cuisine: extractedData.cuisine || '',
    };
    setEditingItem(normalizedData);
    setShowUploadForm(true);
    setShowImageImport(false);
    setShowUrlExtractor(false);
    setShowImportExplainer(false);
    scrollToForm();
    
    // Show confidence info if available
    if (extractedData._extractionConfidence) {
      const confidence = extractedData._extractionConfidence;
      const missingFields = confidence.missingFields || [];
      if (missingFields.length > 0) {
        toast(`Some fields couldn't be extracted: ${missingFields.join(', ')}. Please review and complete the form.`, {
          duration: 5000,
          icon: '⚠️'
        });
      }
    }
  };

  const handleEdit = (item) => {
    if (item.moderation_status === 'APPROVED') {
      setEditConfirm({ isOpen: true, item });
      return;
    }
    setEditingItem(item);
    setShowUploadForm(true);
    setShowUrlExtractor(false);
    setShowImageImport(false);
    setShowImportExplainer(false);
    scrollToForm();
  };

  const handleEditConfirm = () => {
    const item = editConfirm.item;
    setEditConfirm({ isOpen: false, item: null });
    setEditingItem(item);
    setShowUploadForm(true);
    setShowUrlExtractor(false);
    setShowImageImport(false);
    setShowImportExplainer(false);
    scrollToForm();
  };

  const handleViewDetails = (menuId) => {
    navigate(`/menu/${menuId}?context=affiliate`);
  };

  const handleShare = async (menuId) => {
    try {
      // Construct the full URL for the menu item
      const menuUrl = `${window.location.origin}/menu/${menuId}`;
      
      // Copy to clipboard using the Clipboard API
      await navigator.clipboard.writeText(menuUrl);
      
      // Show success message
      toast.success('Menu URL copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy URL');
    }
  };

  const handleDeleteClick = (menuId, title) => {
    setDeleteConfirm({ isOpen: true, menuId, title });
  };

  const handleDeleteConfirm = async () => {
    const { menuId } = deleteConfirm;
    setIsDeleting(true);

    try {
      const token = localStorage.getItem('token');
      // Find the menu item to get its _id
      const menuItem = menuItems.find(item => item.menuId === menuId);
      if (!menuItem) {
        throw new Error('Menu item not found');
      }

      const url = `${process.env.REACT_APP_API_URL}/affiliate/menu-items/${menuItem._id}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete menu item');
      }

      setMenuItems(prev => prev.filter(item => item.menuId !== menuId));
      toast.success('Menu item deleted successfully');
      setDeleteConfirm({ isOpen: false, menuId: null, title: '' });
    } catch (err) {
      console.error('Error deleting menu item:', err);
      toast.error('Failed to delete menu item');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleShowImportExplainer = () => {
    // Close other panels first
    setShowUploadForm(false);
    setShowUrlExtractor(false);
    setShowImageImport(false);
    setEditingItem(null);
    setShowImportExplainer(true);
    scrollToForm();
  };

  const handleStartImport = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Check for existing imports first
      const importStatusResponse = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/import-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!importStatusResponse.ok) {
        throw new Error('Failed to check import status');
      }

      const importHistory = await importStatusResponse.json();
      
      // If there are any previous imports, show confirmation dialog
      if (importHistory.length > 0) {
        setImportConfirm({
          isOpen: true,
          callback: () => proceedWithImport()
        });
        return;
      }

      await proceedWithImport();
    } catch (err) {
      console.error('Error starting import:', err);
      setError('Failed to start import');
      setLoading(false);
    }
  };

  const proceedWithImport = async () => {
    try {
      const token = localStorage.getItem('token');
      setImportConfirm({ isOpen: false, callback: null });
      setLoading(true);
      setError(null);
      setImportProgress(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/import-menus`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to import menus');
      }

      // Start polling for progress
      setIsImporting(true);
      setShowImportExplainer(false);
    } catch (err) {
      setError(err.message);
      setIsImporting(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatPaymentDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return dateString || 'N/A';
    }
  };

  const creatorTabs = [
    { id: 'overview', label: 'Overview', icon: 'fas fa-chart-line' },
    { id: 'menus', label: 'Menus', icon: 'fas fa-utensils' },
    { id: 'creator-page', label: 'Creator Page', icon: 'fas fa-palette' },
    { id: 'metrics', label: 'Metrics', icon: 'fas fa-chart-bar' },
    { id: 'usage', label: 'Usage', icon: 'fas fa-tachometer-alt' },
    { id: 'profile', label: 'Profile', icon: 'fas fa-user-cog' }
  ];

  if (loading) {
    return (
      <div className="creator-dashboard">
        <div className="creator-dashboard-container">
          <div className="creator-loading">
            <div className="loading-spinner"></div>
            <p>Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="creator-dashboard">
        <div className="creator-dashboard-container">
          <div className="error">
            <p>{error}</p>
            <button className="retry-button" onClick={fetchData}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="creator-dashboard">
      <div className="creator-dashboard-container">
        <div className="creator-dashboard-header">
          <div className="creator-header-top">
            <h1>Creator Dashboard</h1>
            <span className={`tier-badge ${getAffiliateTierBadge().className}`}>
              {getAffiliateTierBadge().label}
            </span>
          </div>
          <p>Manage your menus, creator page, and performance</p>
        </div>

        <div className="creator-tabs">
          {creatorTabs.map(tab => (
            <button
              key={tab.id}
              className={`creator-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={tab.icon}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ── Overview Tab ── */}
        {activeTab === 'overview' && (
          <div className="tab-content">
            {usageStats && (
              <div className="creator-stats-grid">
                <div className="creator-stat-card">
                  <div className="creator-stat-icon"><i className="fas fa-utensils"></i></div>
                  <div className="creator-stat-value">{usageStats.usage?.menusUsed || 0}</div>
                  <div className="creator-stat-label">Menus</div>
                  <div className="creator-stat-sub">{usageStats.usage?.menusRemaining || 0} remaining</div>
                </div>
                <div className="creator-stat-card">
                  <div className="creator-stat-icon"><i className="fas fa-eye"></i></div>
                  <div className="creator-stat-value">{metrics?.totalViews || 0}</div>
                  <div className="creator-stat-label">Menu Views</div>
                </div>
                <div className="creator-stat-card">
                  <div className="creator-stat-icon"><i className="fas fa-shopping-cart"></i></div>
                  <div className="creator-stat-value">{metrics?.totalCartAdds || 0}</div>
                  <div className="creator-stat-label">Cart Adds</div>
                </div>
                <div className="creator-stat-card">
                  <div className="creator-stat-icon"><i className="fas fa-dollar-sign"></i></div>
                  <div className="creator-stat-value">${(metrics?.totalPayout || 0).toFixed(2)}</div>
                  <div className="creator-stat-label">Earnings</div>
                </div>
                <div className="creator-stat-card clickable" onClick={() => setActiveTab('usage')}>
                  <div className="creator-stat-icon"><i className="fas fa-tachometer-alt"></i></div>
                  <div className="creator-stat-value">{usageStats.usage?.menusRemaining || 0}</div>
                  <div className="creator-stat-label">Menu Slots Left</div>
                  <div className="creator-stat-sub">View all usage →</div>
                </div>
              </div>
            )}

            <div className="creator-quick-actions">
              <h3>Quick Actions</h3>
              <div className="quick-actions-grid">
                <button className="quick-action-card" onClick={() => setActiveTab('menus')}>
                  <i className="fas fa-plus"></i>
                  <span>Add Menu Item</span>
                </button>
                <button className="quick-action-card" onClick={() => setActiveTab('creator-page')}>
                  <i className="fas fa-palette"></i>
                  <span>Customize Page</span>
                </button>
                <button className="quick-action-card" onClick={() => setActiveTab('metrics')}>
                  <i className="fas fa-chart-bar"></i>
                  <span>View Metrics</span>
                </button>
                {metrics && ['fitness', 'both', 'nutrition'].includes(metrics.specialization) && (
                  <button className="quick-action-card" onClick={() => navigate('/coach/dashboard')}>
                    <i className={`fas ${metrics.specialization === 'nutrition' ? 'fa-users' : 'fa-dumbbell'}`}></i>
                    <span>Coach Dashboard</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Profile Tab ── */}
        {activeTab === 'profile' && (
          <div className="tab-content">
            <section className="affiliate-profile-section">
              <div className="profile-header">
                <h3><i className="fas fa-user-circle"></i> Your Profile</h3>
                {!isEditingProfile && (
                  <button className="edit-profile-btn" onClick={() => setIsEditingProfile(true)}>
                    <i className="fas fa-edit"></i> Edit Profile
                  </button>
                )}
              </div>

        {isEditingProfile ? (
          <div className="profile-edit-form">
            <div className="edit-form-grid">
              <div className="edit-form-group">
                <label>Business Name</label>
                <input
                  type="text"
                  name="businessName"
                  value={profileForm.businessName}
                  onChange={handleProfileInputChange}
                  placeholder="Your business or brand name"
                />
              </div>

              <div className="edit-form-group">
                <label>Business Type</label>
                <select
                  name="businessType"
                  value={profileForm.businessType}
                  onChange={handleProfileInputChange}
                >
                  <option value="">Select type...</option>
                  <option value="BLOG">Blog</option>
                  <option value="SOCIAL_MEDIA">Social Media</option>
                  <option value="WEBSITE">Website</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div className="edit-form-group full-width">
                <label>Website URL</label>
                <input
                  type="url"
                  name="website"
                  value={profileForm.website}
                  onChange={handleProfileInputChange}
                  placeholder="https://your-website.com"
                />
              </div>

              <div className="edit-form-group">
                <label><i className="fab fa-instagram"></i> Instagram</label>
                <input
                  type="text"
                  name="socialMedia.instagram"
                  value={profileForm.socialMedia.instagram}
                  onChange={handleProfileInputChange}
                  placeholder="@username"
                />
              </div>

              <div className="edit-form-group">
                <label><i className="fab fa-tiktok"></i> TikTok</label>
                <input
                  type="text"
                  name="socialMedia.tiktok"
                  value={profileForm.socialMedia.tiktok}
                  onChange={handleProfileInputChange}
                  placeholder="@username"
                />
              </div>

              <div className="edit-form-group">
                <label><i className="fab fa-facebook"></i> Facebook</label>
                <input
                  type="text"
                  name="socialMedia.facebook"
                  value={profileForm.socialMedia.facebook}
                  onChange={handleProfileInputChange}
                  placeholder="Page name or URL"
                />
              </div>

              <div className="edit-form-group">
                <label><i className="fab fa-twitter"></i> Twitter/X</label>
                <input
                  type="text"
                  name="socialMedia.twitter"
                  value={profileForm.socialMedia.twitter}
                  onChange={handleProfileInputChange}
                  placeholder="@username"
                />
              </div>

              <div className="edit-form-group full-width">
                <label><i className="fab fa-paypal"></i> PayPal Email (for payments)</label>
                <input
                  type="email"
                  name="paymentInfo.email"
                  value={profileForm.paymentInfo.email}
                  onChange={handleProfileInputChange}
                  placeholder="your-paypal@email.com"
                />
              </div>
            </div>

            <div className="edit-form-actions">
              <button 
                className="cancel-edit-btn"
                onClick={() => setIsEditingProfile(false)}
                disabled={savingProfile}
              >
                Cancel
              </button>
              <button 
                className="save-profile-btn"
                onClick={handleSaveProfile}
                disabled={savingProfile}
              >
                {savingProfile ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i> Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="profile-details">
            <div className="profile-field">
              <label>Business Name</label>
              <p>{metrics?.businessName || <span className="not-set">Not set</span>}</p>
            </div>
            <div className="profile-field">
              <label>Business Type</label>
              <p>{metrics?.businessType || <span className="not-set">Not set</span>}</p>
            </div>
            <div className="profile-field">
              <label>Commission Rate</label>
              <p>{(metrics?.commissionRate * 100).toFixed(1)}%</p>
            </div>
            <div className="profile-field">
              <label>Website</label>
              <p>
                {metrics?.website ? (
                  <a href={metrics.website} target="_blank" rel="noopener noreferrer">
                    {metrics.website}
                  </a>
                ) : (
                  <span className="not-set">Not set</span>
                )}
              </p>
            </div>
            <div className="profile-field">
              <label>Social Media</label>
              <div className="social-media-links">
                {metrics?.socialMedia?.instagram && (
                  <a href={`https://instagram.com/${metrics.socialMedia.instagram}`} target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-instagram"></i> Instagram
                  </a>
                )}
                {metrics?.socialMedia?.facebook && (
                  <a href={`https://facebook.com/${metrics.socialMedia.facebook}`} target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-facebook"></i> Facebook
                  </a>
                )}
                {metrics?.socialMedia?.twitter && (
                  <a href={`https://twitter.com/${metrics.socialMedia.twitter}`} target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-twitter"></i> Twitter
                  </a>
                )}
                {metrics?.socialMedia?.tiktok && (
                  <a href={`https://tiktok.com/@${metrics.socialMedia.tiktok}`} target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-tiktok"></i> TikTok
                  </a>
                )}
                {!metrics?.socialMedia?.instagram && !metrics?.socialMedia?.facebook && 
                 !metrics?.socialMedia?.twitter && !metrics?.socialMedia?.tiktok && (
                  <span className="not-set">No social media linked</span>
                )}
              </div>
            </div>
            <div className="profile-field">
              <label>PayPal Email</label>
              <p>{metrics?.paymentInfo?.email || <span className="not-set">Not set - required for payments</span>}</p>
            </div>
            <div className="profile-field">
              <label>Account Status</label>
              <p className={`status-badge ${metrics?.status?.toLowerCase() || 'pending'}`}>
                {metrics?.status || 'PENDING'}
              </p>
            </div>
          </div>
        )}
            </section>
          </div>
        )}

        {/* ── Menus Tab ── */}
        {activeTab === 'menus' && (
          <div className="tab-content">
            <section className="menu-items-section">
        <div className="section-header">
          <h3><i className="fas fa-utensils"></i> Your Menu Items</h3>
          <div className="menu-actions">
            <button 
              className="add-menu-button"
              onClick={() => {
                setEditingItem(null);
                setShowUploadForm(!showUploadForm);
                setShowUrlExtractor(false);
                setShowImageImport(false);
                setShowImportExplainer(false);
              }}
            >
              <i className="fas fa-plus"></i> {showUploadForm ? 'Cancel' : 'Add Menu Item'}
            </button>
            <button 
              className="extract-menu-button"
              onClick={() => {
                setShowUrlExtractor(!showUrlExtractor);
                setShowUploadForm(false);
                setShowImageImport(false);
                setShowImportExplainer(false);
                setEditingItem(null);
              }}
            >
              <i className="fas fa-link"></i> {showUrlExtractor ? 'Cancel' : 'Extract from URL'}
            </button>
            <button 
              className="image-import-button"
              onClick={() => {
                setShowImageImport(!showImageImport);
                setShowUrlExtractor(false);
                setShowUploadForm(false);
                setShowImportExplainer(false);
                setEditingItem(null);
              }}
            >
              <i className="fas fa-camera"></i> {showImageImport ? 'Cancel' : 'Import from Image'}
            </button>
            <button 
              className="import-menus-button"
              onClick={() => {
                if (showImportExplainer) {
                  setShowImportExplainer(false);
                } else {
                  handleShowImportExplainer();
                }
              }}
              disabled={isImporting}
            >
              <i className="fas fa-file-import"></i> {isImporting ? 'Importing...' : showImportExplainer ? 'Cancel' : 'Import Menus'}
            </button>
          </div>
        </div>

        <div ref={formSectionRef}>
          {showUrlExtractor && (
            <MenuUrlExtractor onExtractSuccess={handleExtractSuccess} />
          )}

          {showImageImport && (
            <MenuImageImport 
              onExtractSuccess={handleImageImportSuccess}
              onCancel={() => setShowImageImport(false)}
            />
          )}

          {showImportExplainer && (
            <div className="import-explainer">
              <div className="explainer-header">
                <div className="explainer-icon">
                  <i className="fas fa-globe"></i>
                </div>
                <h3>Import Menus from Your Website</h3>
                <button 
                  className="explainer-close-btn"
                  onClick={() => setShowImportExplainer(false)}
                  title="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <div className="explainer-content">
                <p className="explainer-description">
                  This feature will automatically scan your registered website and import any recipes or menus it finds.
                </p>
                
                <div className="explainer-details">
                  <div className="explainer-detail-item">
                    <i className="fas fa-link"></i>
                    <div>
                      <strong>Your Website</strong>
                      <span>{metrics?.website || 'No website configured'}</span>
                    </div>
                  </div>
                  
                  <div className="explainer-detail-item">
                    <i className="fas fa-search"></i>
                    <div>
                      <strong>What it does</strong>
                      <span>Scans your site for recipe pages and extracts ingredients, instructions, and images</span>
                    </div>
                  </div>
                  
                  <div className="explainer-detail-item">
                    <i className="fas fa-clock"></i>
                    <div>
                      <strong>How long it takes</strong>
                      <span>Usually 1-5 minutes depending on the number of recipes</span>
                    </div>
                  </div>
                </div>

                {!metrics?.website && (
                  <div className="explainer-warning">
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>You need to have a website configured in your profile to use this feature.</span>
                  </div>
                )}

                <div className="explainer-note">
                  <i className="fas fa-info-circle"></i>
                  <span>After importing, you can edit any menu item to add missing details or make corrections.</span>
                </div>
              </div>

              <div className="explainer-actions">
                <button 
                  className="explainer-cancel-btn"
                  onClick={() => setShowImportExplainer(false)}
                >
                  Cancel
                </button>
                <button 
                  className="explainer-start-btn"
                  onClick={handleStartImport}
                  disabled={!metrics?.website || isImporting || loading}
                >
                  {loading ? (
                    <>
                      <span className="btn-spinner"></span>
                      Starting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search"></i>
                      Start Website Scan
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {showUploadForm && (
            <MenuUploadForm
              onSuccess={handleUploadSuccess}
              onCancel={() => {
                setShowUploadForm(false);
                setEditingItem(null);
              }}
              initialData={editingItem}
            />
          )}
        </div>

        {importProgress && (
          <div className={`import-progress ${importProgress.status}`}>
            <p>{importProgress.message}</p>
            {importProgress.error && (
              <p className="error-message">{importProgress.error}</p>
            )}
          </div>
        )}

        {/* Notice about unmigrated personal menus */}
        {unmigratedMenusCount > 0 && (
          <div className="unmigrated-menus-notice">
            <div className="notice-content">
              <i className="fas fa-info-circle"></i>
              <div className="notice-text">
                <strong>You have {unmigratedMenusCount} personal menu{unmigratedMenusCount > 1 ? 's' : ''} that can be migrated to your creator dashboard.</strong>
                <p>These menus will be added as drafts - you can review and publish them when ready.</p>
              </div>
              <button 
                className="migrate-btn"
                onClick={handleMigratePersonalMenus}
                disabled={isMigratingMenus}
              >
                {isMigratingMenus ? 'Migrating...' : `Migrate Menu${unmigratedMenusCount > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}

        {/* Add search bar */}
        <div className="affiliate-search-container">
          <input
            type="text"
            placeholder="Search by name, description, or ingredients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="affiliate-search-input"
          />
        </div>

        <div className="menu-items-grid">
          {filteredMenuItems.map(item => {
            const status = item.moderation_status || 'DRAFT';
            const statusClass = `status-${status.toLowerCase()}`;
            const statusLabels = {
              DRAFT: { label: 'Draft (Not Public)', icon: 'fa-file-alt', description: 'Only you can see this menu' },
              PENDING: { label: 'Pending Review', icon: 'fa-clock', description: 'Waiting for admin approval' },
              APPROVED: { label: 'Public', icon: 'fa-check-circle', description: 'Visible to everyone' },
              REJECTED: { label: 'Rejected', icon: 'fa-times-circle', description: 'Not approved for public display' }
            };
            const statusInfo = statusLabels[status] || statusLabels.DRAFT;
            const isFromPersonalMenu = item.isPersonal === true;
            
            return (
            <div key={item._id} className={`menu-item-card ${statusClass}`}>
              <img 
                src={item.image} 
                alt={item.title} 
                className="item-image"
                onClick={() => handleViewDetails(item.menuId)}
              />
              <div className="item-details">
                <h3>{item.title}</h3>
                {isFromPersonalMenu && (
                  <span className="migrated-badge" title="Migrated from personal menus">
                    <i className="fas fa-exchange-alt"></i> From Personal
                  </span>
                )}
                <p>{item.description}</p>
                <p className="cuisine">Cuisine: {item.cuisine}</p>
                <div className={`status-badge ${statusClass}`} title={statusInfo.description}>
                  <i className={`fas ${statusInfo.icon}`}></i>
                  <span>{statusInfo.label}</span>
                </div>
                <div className="metrics-row">
                  <div className="metric">
                    <span className="metric-label">Views:</span>
                    <span className="metric-value">{item.metrics?.views || 0}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Cart Adds:</span>
                    <span className="metric-value">{item.metrics?.cartAdds || 0}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Orders:</span>
                    <span className="metric-value">{item.metrics?.orders || 0}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Official Orders:</span>
                    <span className="metric-value">{item.metrics?.affiliateOrders || 0}</span>
                  </div>
                </div>
                <div className="item-actions">
                  {/* Publish button for drafts */}
                  {status === 'DRAFT' && (
                    <button
                      className="publish-button"
                      onClick={() => handlePublishMenu(item._id)}
                      disabled={isPublishing === item._id}
                      title="Submit for review to make this menu public"
                    >
                      {isPublishing === item._id ? (
                        <><i className="fas fa-spinner fa-spin"></i> Publishing...</>
                      ) : (
                        <><i className="fas fa-globe"></i> Make Public</>
                      )}
                    </button>
                  )}
                  <button
                    className="view-details-button"
                    onClick={() => handleViewDetails(item.menuId)}
                  >
                    View Details
                  </button>
                  <button
                    className="edit-button"
                    onClick={() => handleEdit(item)}
                  >
                    {status === 'APPROVED' ? (
                      <><i className="fas fa-edit"></i> Edit &amp; Resubmit</>
                    ) : (
                      'Edit'
                    )}
                  </button>
                  {status === 'APPROVED' && (
                    <button 
                      className="share-button"
                      onClick={() => handleShare(item.menuId)}
                      title="Copy menu URL to clipboard"
                    >
                      <i className="fas fa-share-alt"></i> Share
                    </button>
                  )}
                  <button 
                    className="delete-button"
                    onClick={() => handleDeleteClick(item.menuId, item.title)}
                    title="Delete menu item"
                  >
                    <i className="fas fa-trash"></i> Delete
                  </button>
                </div>
              </div>
            </div>
          )})}
        </div>
            </section>
          </div>
        )}

        {/* ── Creator Page Tab ── */}
        {activeTab === 'creator-page' && (
          <div className="tab-content">
            <section className="affiliate-link-section page-customization-section">
        <h3><i className="fas fa-palette"></i> Customize Your Creator Page</h3>
        <p className="link-description">
          Set up your branded page where visitors can browse all your recipes. Customize the look and share your unique link.
        </p>

        {/* Creator Page URL */}
        <div className="page-setting-group">
          <label className="page-setting-label">Your Page URL</label>
          <div className="slug-input-row">
            <span className="slug-prefix">{window.location.origin}/creator/</span>
            <input
              type="text"
              className="slug-input"
              value={pageSettings.slug}
              onChange={(e) => {
                const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 60);
                setPageSettings(prev => ({ ...prev, slug: val }));
                setSlugAvailable(null);
              }}
              onBlur={() => checkSlugAvailability(pageSettings.slug)}
              placeholder="your-page-name"
            />
            {checkingSlug && <i className="fas fa-spinner fa-spin slug-status"></i>}
            {!checkingSlug && slugAvailable === true && <i className="fas fa-check slug-status slug-available"></i>}
            {!checkingSlug && typeof slugAvailable === 'string' && <i className="fas fa-times slug-status slug-taken"></i>}
          </div>
          {typeof slugAvailable === 'string' && <p className="slug-error">{slugAvailable}</p>}
        </div>

        <div className="page-setting-row">
          <button
            className="copy-button"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/creator/${pageSettings.slug}`);
              toast.success('Creator page link copied!');
            }}
            disabled={!pageSettings.slug}
          >
            <i className="fas fa-copy"></i> Copy Link
          </button>
          <a
            href={`/creator/${pageSettings.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="copy-button view-page-btn"
            style={{ textDecoration: 'none', display: pageSettings.slug ? 'inline-flex' : 'none' }}
          >
            <i className="fas fa-external-link-alt"></i> View Page
          </a>
        </div>

        {/* Headline */}
        <div className="page-setting-group">
          <label className="page-setting-label">Headline</label>
          <input
            type="text"
            className="page-setting-input"
            value={pageSettings.headline}
            onChange={(e) => setPageSettings(prev => ({ ...prev, headline: e.target.value.slice(0, 120) }))}
            placeholder="e.g. Healthy recipes for busy families"
            maxLength={120}
          />
          <span className="page-setting-counter">{pageSettings.headline.length}/120</span>
        </div>

        {/* Bio */}
        <div className="page-setting-group">
          <label className="page-setting-label">Bio</label>
          <textarea
            className="page-setting-textarea"
            value={pageSettings.bio}
            onChange={(e) => setPageSettings(prev => ({ ...prev, bio: e.target.value.slice(0, 500) }))}
            placeholder="Tell visitors about yourself and your recipes..."
            maxLength={500}
            rows={3}
          />
          <span className="page-setting-counter">{pageSettings.bio.length}/500</span>
        </div>

        {/* Logo Upload */}
        <div className="page-setting-group">
          <label className="page-setting-label">Logo / Profile Image</label>
          <div className="image-upload-area">
            {pageSettings.logoImage ? (
              <div className="image-preview-row">
                <img src={pageSettings.logoImage} alt="Logo" className="image-preview-logo" />
                <button className="image-remove-btn" onClick={() => setPageSettings(prev => ({ ...prev, logoImage: '' }))}>
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            ) : (
              <p className="image-upload-hint">No logo uploaded</p>
            )}
            <label className="image-upload-btn">
              {uploadingLogo ? <><i className="fas fa-spinner fa-spin"></i> Uploading...</> : <><i className="fas fa-upload"></i> Upload Logo</>}
              <input
                type="file"
                accept="image/jpeg,image/png"
                style={{ display: 'none' }}
                onChange={(e) => e.target.files[0] && handleUploadPageImage(e.target.files[0], 'logo')}
                disabled={uploadingLogo}
              />
            </label>
          </div>
        </div>

        {/* Hero Image */}
        <div className="page-setting-group">
          <label className="page-setting-label">Cover Image</label>
          <div className="image-upload-area">
            {pageSettings.heroImage ? (
              <div className="image-preview-row">
                <img src={pageSettings.heroImage} alt="Cover" className="image-preview-hero" />
                <button className="image-remove-btn" onClick={() => setPageSettings(prev => ({ ...prev, heroImage: '' }))}>
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            ) : (
              <p className="image-upload-hint">No cover image selected</p>
            )}
            <label className="image-upload-btn">
              {uploadingHero ? <><i className="fas fa-spinner fa-spin"></i> Uploading...</> : <><i className="fas fa-upload"></i> Upload Image</>}
              <input
                type="file"
                accept="image/jpeg,image/png"
                style={{ display: 'none' }}
                onChange={(e) => e.target.files[0] && handleUploadPageImage(e.target.files[0], 'hero')}
                disabled={uploadingHero}
              />
            </label>
          </div>
          <p className="page-setting-label" style={{ marginTop: '12px', marginBottom: '8px' }}>Or choose a stock image:</p>
          <div className="stock-image-grid">
            {STOCK_HERO_IMAGES.map((url, i) => (
              <div
                key={i}
                className={`stock-image-thumb ${pageSettings.heroImage === url ? 'stock-image-selected' : ''}`}
                onClick={() => setPageSettings(prev => ({ ...prev, heroImage: url }))}
              >
                <img
                  src={url}
                  alt={`Stock ${i + 1}`}
                  loading="lazy"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                {pageSettings.heroImage === url && <i className="fas fa-check-circle stock-check"></i>}
              </div>
            ))}
          </div>
        </div>

        {/* Theme Picker */}
        <div className="page-setting-group">
          <label className="page-setting-label">Theme</label>
          <div className="theme-picker-grid">
            {THEME_OPTIONS.map(t => (
              <div
                key={t.id}
                className={`theme-option theme-option--${t.id} ${pageSettings.theme === t.id ? 'theme-option-active' : ''}`}
                onClick={() => setPageSettings(prev => ({ ...prev, theme: t.id }))}
              >
                <div className="theme-swatch" />
                <span className="theme-label">{t.label}</span>
                <span className="theme-desc">{t.desc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <button
          className="copy-button save-page-btn"
          onClick={handleSavePageSettings}
          disabled={savingPageSettings}
        >
          {savingPageSettings ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-save"></i> Save Page Settings</>}
              </button>
            </section>
          </div>
        )}

        {/* ── Metrics Tab ── */}
        {activeTab === 'metrics' && (
          <div className="tab-content">
            <section className="metrics-section">
        <h3><i className="fas fa-chart-bar"></i> Performance Metrics</h3>

        {/* FreshFitLab Metrics */}
        <div className="metrics-group freshmenulab">
          <h4 className="metrics-group-title">
            <i className="fas fa-chart-line"></i> FreshFitLab Activity
            <span className="metrics-subtitle">User engagement with your menu items on FreshFitLab</span>
          </h4>
          <div className="metrics-grid">
            <div className="metric-card">
              <h3>Menu Views</h3>
              <div className="metric-value">{metrics?.totalViews || 0}</div>
              <div className="metric-description">Times users viewed your menu items</div>
            </div>
            <div className="metric-card">
              <h3>Added to Cart</h3>
              <div className="metric-value">{metrics?.totalCartAdds || 0}</div>
              <div className="metric-description">Times users added a menu to their shopping cart</div>
            </div>
            <div className="metric-card">
              <h3>Ordered via Instacart</h3>
              <div className="metric-value">{metrics?.totalOrders || 0}</div>
              <div className="metric-description">Times users clicked "Buy on Instacart"</div>
            </div>
          </div>
        </div>

        {/* Impact.com Official Metrics */}
        <div className="metrics-group impact">
          <h4 className="metrics-group-title">
            <i className="fas fa-dollar-sign"></i> Official Orders & Commission
            <span className="metrics-subtitle">Verified orders and earnings through Impact.com</span>
          </h4>
          <div className="metrics-grid">
            <div className="metric-card">
              <h3>Verified Orders</h3>
              <div className="metric-value">{metrics?.totalAffiliateOrders || 0}</div>
              <div className="metric-description">Completed purchases through Instacart</div>
            </div>
            <div className="metric-card">
              <h3>Total Sales</h3>
              <div className="metric-value">${(metrics?.totalSales || 0).toFixed(2)}</div>
              <div className="metric-description">Value of verified orders</div>
            </div>
            <div className="metric-card">
              <h3>Commission Earned</h3>
              <div className="metric-value">${(metrics?.totalPayout || 0).toFixed(2)}</div>
              <div className="metric-description">Your earnings from verified orders</div>
            </div>
          </div>

          {/* Orders Table */}
          <div className="orders-table-container">
            <h5>Recent Orders</h5>
            {ordersLoading ? (
              <div className="loading">Loading orders...</div>
            ) : (
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Sale Amount</th>
                    <th>Your Payout</th>
                    <th>Menu Item</th>
                    <th>Landing Page</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => (
                    <tr key={index}>
                      <td>{formatDate(order.action_date)}</td>
                      <td>
                        <span className={`status-badge ${order.status.toLowerCase()}`}>
                          {order.status}
                        </span>
                      </td>
                      <td>{formatCurrency(order.sale_amount)}</td>
                      <td>{formatCurrency(order.payout)}</td>
                      <td>
                        {order.menuItem ? (
                          <Link 
                            to={`/menu/${order.menuItem.menuId}`}
                            className="menu-link"
                          >
                            {order.menuItem.title}
                          </Link>
                        ) : (
                          <span className="no-menu">No menu item</span>
                        )}
                      </td>
                      <td>
                        <a 
                          href={order.ref_landingpage_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="url-link"
                        >
                          View URL
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="commission-info-section">
          <h4>Commission & Payout Information</h4>
          <div className="commission-details">
            <div className="info-item">
              <i className="fas fa-percentage"></i>
              <div>
                <h5>Your Commission Rate</h5>
                <p>You earn {(metrics?.commissionRate * 100).toFixed(1)}% commission on every verified order through Impact.com.</p>
              </div>
            </div>
            <div className="info-item">
              <i className="fas fa-calculator"></i>
              <div>
                <h5>How It's Calculated</h5>
                <p>For each verified order, your commission is calculated as:<br />
                <code>Sale Amount × {(metrics?.commissionRate * 100).toFixed(1)}%</code></p>
              </div>
            </div>
            <div className="info-item">
              <i className="fas fa-money-bill-wave"></i>
              <div>
                <h5>Payout Schedule</h5>
                <p>Payouts are processed every 2 weeks to your registered payment method when your balance exceeds $10 USD.</p>
              </div>
            </div>
            <div className="info-item">
              <i className="fas fa-info-circle"></i>
              <div>
                <h5>Minimum Payout</h5>
                <p>If your balance is less than $10 USD, it will roll over to the next payout period until the minimum is reached.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="metrics-explanation">
          <i className="fas fa-info-circle"></i> 
          <div>
            <p><strong>FreshFitLab Activity:</strong> Shows how users interact with your content on our platform. Menu views and ingredient list opens help track engagement before purchases.</p>
            <p><strong>Official Orders & Commission:</strong> Tracks verified purchases and earnings through Impact.com's tracking system. Only these orders count towards your commission.</p>
          </div>
        </div>

        <div className="metrics-group payments">
          <h4 className="metrics-group-title">
            <i className="fas fa-money-bill"></i> Payments & Commission
          </h4>
          
          <div className="payments-section">
            <h2>Payment History</h2>
            <div className="date-filters">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleDateChange}
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleDateChange}
                />
              </div>
            </div>

            <div className="payments-table-container">
              {payments?.items && Array.isArray(payments.items) && payments.items.length > 0 ? (
                <table className="payments-table">
                  <thead>
                    <tr>
                      <th>Date Paid</th>
                      <th>Amount</th>
                      <th>Period</th>
                      <th>Status</th>
                      <th>Payment Method</th>
                      <th>PayPal Transaction</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.items.map((payment) => (
                      <tr key={payment._id}>
                        <td>{formatPaymentDate(payment.paidAt)}</td>
                        <td>{formatCurrency(payment.amount)}</td>
                        <td>
                          {formatPaymentDate(payment.periodStart)} - {formatPaymentDate(payment.periodEnd)}
                        </td>
                        <td>
                          <span className={`status-badge ${payment.status.toLowerCase()}`}>
                            {payment.status}
                          </span>
                        </td>
                        <td>{payment.paymentMethod}</td>
                        <td>
                          {payment.paypalTransactionId ? (
                            <a
                              href={`https://www.paypal.com/activity/payment/${payment.paypalTransactionId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="transaction-link"
                            >
                              View Transaction
                            </a>
                          ) : (
                            'N/A'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="6">
                        <div className="payment-summary">
                          <span>Total Earnings: {formatCurrency(payments.summary.totalEarnings || 0)}</span>
                          <span>Total Paid: {formatCurrency(payments.summary.totalPaid || 0)}</span>
                          <span>Unpaid Balance: {formatCurrency(payments.summary.unpaidBalance || 0)}</span>
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div className="no-payments">
                  <p>No payments found for the selected date range.</p>
                  <p>Try adjusting the date range to see more payments.</p>
                  {payments && !Array.isArray(payments.items) && (
                    <p className="error-message">Invalid payment data received from server.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
            </section>
          </div>
        )}

        {/* ── Usage Tab ── */}
        {activeTab === 'usage' && (
          <div className="tab-content">
            <section className="usage-tab-section">
              <div className="usage-tab-header">
                <h2><i className="fas fa-tachometer-alt"></i> Plan Usage</h2>
                <span className="usage-tier-badge">
                  {subscriptionInfo?.affiliate?.tier === 'pro' ? 'Pro Creator' : 'Free Creator'}
                </span>
              </div>

              <div className="usage-table-wrapper">
                <table className="usage-table">
                  <thead>
                    <tr>
                      <th>Resource</th>
                      <th>Used</th>
                      <th>Limit</th>
                      <th>Remaining</th>
                      <th>Usage</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="usage-resource"><i className="fas fa-utensils"></i> Menus</td>
                      <td>{usageStats?.usage?.menusUsed || 0}</td>
                      <td>{usageStats?.usage?.menusLimit || usageStats?.limits?.maxMenus || '—'}</td>
                      <td>{usageStats?.usage?.menusRemaining ?? '—'}</td>
                      <td>
                        <div className="usage-bar">
                          <div className="usage-bar-fill" style={{ width: `${Math.min(100, ((usageStats?.usage?.menusUsed || 0) / (usageStats?.usage?.menusLimit || 1)) * 100)}%` }} />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="usage-resource"><i className="fas fa-magic"></i> AI Generations <span className="usage-period">/ day</span></td>
                      <td>{usageStats?.usage?.aiGenerationsToday || 0}</td>
                      <td>{usageStats?.usage?.aiGenerationsLimit || usageStats?.limits?.maxAIGenerationsPerDay || '—'}</td>
                      <td>{usageStats?.usage?.aiGenerationsRemaining ?? '—'}</td>
                      <td>
                        <div className="usage-bar">
                          <div className="usage-bar-fill" style={{ width: `${Math.min(100, ((usageStats?.usage?.aiGenerationsToday || 0) / (usageStats?.usage?.aiGenerationsLimit || 1)) * 100)}%` }} />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="usage-resource"><i className="fas fa-calendar-alt"></i> MealPlanify <span className="usage-period">/ month</span></td>
                      <td>{usageStats?.usage?.mealPlanifyThisMonth || 0}</td>
                      <td>{usageStats?.usage?.mealPlanifyLimit || usageStats?.limits?.maxMealPlanifyPerMonth || '—'}</td>
                      <td>{usageStats?.usage?.mealPlanifyRemaining ?? '—'}</td>
                      <td>
                        <div className="usage-bar">
                          <div className="usage-bar-fill" style={{ width: `${Math.min(100, ((usageStats?.usage?.mealPlanifyThisMonth || 0) / (usageStats?.usage?.mealPlanifyLimit || 1)) * 100)}%` }} />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="usage-resource"><i className="fas fa-upload"></i> Image Uploads <span className="usage-period">/ day</span></td>
                      <td>—</td>
                      <td>{usageStats?.limits?.maxImageUploadsPerDay || '—'}</td>
                      <td>—</td>
                      <td><div className="usage-bar"><div className="usage-bar-fill" style={{ width: '0%' }} /></div></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="usage-info-note">
                <i className="fas fa-info-circle"></i>
                <p>Daily limits reset at midnight UTC. Monthly limits reset on the 1st of each month. Upgrade to Pro for higher limits.</p>
              </div>
            </section>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, menuId: null, title: '' })}
        onConfirm={handleDeleteConfirm}
        title="Delete Menu Item"
        message={`Are you sure you want to delete "${deleteConfirm.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Import Overwrite Confirmation Modal */}
      <ConfirmModal
        isOpen={importConfirm.isOpen}
        onClose={() => setImportConfirm({ isOpen: false, callback: null })}
        onConfirm={() => importConfirm.callback && importConfirm.callback()}
        title="Overwrite Existing Menus?"
        message="Previous menu imports were found. Importing menus again will overwrite any existing imported menus and any edits made to them within FreshFitLab. Are you sure you want to proceed?"
        confirmText="Proceed with Import"
        cancelText="Cancel"
        type="warning"
      />

      {/* Edit Approved Menu Confirmation Modal */}
      <ConfirmModal
        isOpen={editConfirm.isOpen}
        onClose={() => setEditConfirm({ isOpen: false, item: null })}
        onConfirm={handleEditConfirm}
        title="Edit Published Menu?"
        message="This menu is currently public. Editing it will take it offline and send the updated version for re-review. It won't be visible to users until approved again. Do you want to continue?"
        confirmText="Edit & Resubmit"
        cancelText="Cancel"
        type="warning"
      />

      {/* Fitness Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={fitnessDeleteConfirm.isOpen}
        onClose={() => setFitnessDeleteConfirm({ isOpen: false, type: '', id: null, name: '' })}
        onConfirm={handleFitnessDelete}
        title={`Delete ${fitnessDeleteConfirm.type}?`}
        message={`Are you sure you want to delete "${fitnessDeleteConfirm.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeletingFitness}
      />
    </div>
  );
};

export default AffiliateDashboard; 